import { useState } from "react";

import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { resetPasswordForEmail } from "@/api/auth";
import { logger } from "@/lib/logger";
import { getErrorMessage } from "@/lib/getErrorMessage";
import { cn } from "@/lib/utils";
import { AuthCard } from "./AuthCard";
import { ValidatedField } from "./ValidatedField";
import { useFieldValidation } from "./useFieldValidation";
import { SuccessIcon } from "@/components/icons/status";
import { MailIcon } from "@/components/icons/actions";
import { InboxIcon } from "@/components/icons/domain";

const emailSchema = z.string().email("Email invalide");

interface ForgotPasswordFormProps {
  onBackToLogin: () => void;
}

export function ForgotPasswordForm({ onBackToLogin }: ForgotPasswordFormProps) {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const emailValidation = useFieldValidation({ value: email, schema: emailSchema, required: true });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    emailValidation.markTouched();
    if (emailValidation.state === "invalid" || !email.trim()) return;

    setIsLoading(true);
    try {
      const { error } = await resetPasswordForEmail(email, {
        // Origin courant pour rester sur l'environnement de la demande.
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setSent(true);
    } catch (err: unknown) {
      logger.error("Auth forgot-password error:", err);
      toast.error("Erreur", { description: getErrorMessage(err, "Une erreur est survenue. Veuillez réessayer.") });
    } finally {
      setIsLoading(false);
    }
  };

  if (sent) {
    return (
      <AuthCard
        accentLastWord={false}
        title="Email envoyé !"
        subtitle={
          <>
            Si un compte existe avec l'adresse <strong className="text-foreground">{email}</strong>, vous recevrez un lien
            de réinitialisation.
          </>
        }
        bottomSlot={
          <Button variant="outline" type="button" onClick={onBackToLogin} className="w-full">
            Retour à la connexion
          </Button>
        }
      >
        <div className="space-y-5 text-center">
          <div className="relative w-16 h-16 mx-auto">
            <div className="absolute inset-0 rounded-full bg-success/15 motion-safe:animate-pulse" />
            <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-success/20 to-success/5 flex items-center justify-center shadow-[0_0_40px_hsl(var(--success)/0.25)]">
              <SuccessIcon className="w-8 h-8 text-success" strokeWidth={2.5} aria-hidden="true" />
            </div>
          </div>
          <div className="rounded-xl bg-warning/[0.08] border border-warning/25 p-3.5 text-left flex gap-3">
            <InboxIcon className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-xs text-foreground/80 leading-relaxed">
              Vous ne trouvez pas l'email ? Vérifiez votre dossier <strong>Spam</strong> ou <strong>Courriers indésirables</strong>. Ajoutez{" "}
              <strong>noreply@equiply.fr</strong> à vos contacts.
            </p>
          </div>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Mot de passe oublié"
      subtitle="Entrez votre email pour recevoir un lien de réinitialisation."
      bottomSlot={
        <Button type="button" variant="link" onClick={onBackToLogin} className="px-0 h-auto text-sm">
          ← Retour à la connexion
        </Button>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <ValidatedField
          label="Email"
          type="email"
          inputMode="email"
          leadingIcon={<MailIcon />}
          placeholder="jean.dupont@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={emailValidation.onBlur}
          disabled={isLoading}
          autoComplete="email"
          validation={emailValidation}
        />
        <Button
          type="submit"
          loading={isLoading}
          className={cn(
            "w-full h-12 text-base font-semibold rounded-xl mt-2",
            "bg-gradient-to-r from-primary to-accent hover:shadow-elevated hover:scale-[1.01]",
            "transition-[transform,box-shadow] duration-base ease-out-expo",
            "[&_*]:!text-primary-foreground",
          )}
        >
          {isLoading ? "Envoi en cours…" : "Envoyer le lien"}
        </Button>
      </form>
    </AuthCard>
  );
}
